import React, { useEffect, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { X, ArrowRight } from "lucide-react";
import Chatbot from "@/components/site/Chatbot";

/**
 * SiteGuide — mini-Julius, the cartoon mascot. On a visitor's first session he
 * slides in and gives a one-time 4-stop tour. Once the tour is finished or
 * closed, he permanently becomes the site assistant: clicking him opens the
 * chat window instead of replaying the tour.
 */

const TOUR = [
  { text: "Hey, I'm Julius — welcome! Here's how getting your website or app built works, in three simple steps.", link: null },
  { text: "Step 1: describe what you need and watch it take shape live. Step 2: we refine it together until it's exactly right. Step 3: I build, launch and host the real thing — you just approve.", link: { to: "/build", label: "Start step 1" } },
  { text: "Stores, bookings, portfolios, memberships, blogs, SEO — whatever features your business needs get built in from day one.", link: { to: "/services", label: "See what's possible" } },
  { text: "And pricing is simple: $0 upfront. Sign a quick agreement, try everything free for 30 days — then one flat monthly rate covers your build, hosting and care.", link: { to: "/pricing", label: "See pricing" } },
];

const STORAGE_KEY = "site_guide_state"; // "dismissed" = tour over, chat mode forever
const NUDGED_KEY = "jm_nudged"; // sessionStorage list of paths already nudged

// Contextual tips Julius offers while browsing (chat mode only, once per page per session).
const NUDGES = [
  { match: "/services", text: "Wondering what your project would cost? The instant quote takes 30 seconds.", link: { to: "/pricing", label: "Get a quote" } },
  { match: "/pricing", text: "Want to see your site before you commit? Describe it and watch it take shape.", link: { to: "/build", label: "Build my website" } },
  { match: "/clients", text: "Your company could be listed here — I build and support programs and teams.", link: { to: "/start-project", label: "Start a project" } },
  { match: "/tools", text: "Every tool here has a step-by-step guide on the blog.", link: { to: "/blog", label: "Read the guides" } },
  { match: "/blog", text: "Found this useful? There are 149 free tools waiting for you.", link: { to: "/tools", label: "Browse tools" } },
  { match: "/shop", text: "Everything here is gear I actually use — and my own brand AVENO lives here too.", link: { to: "/aveno", label: "Meet AVENO" } },
  { match: "/aveno", text: "Like the brand? The whole collection is in the Shop.", link: { to: "/shop", label: "Shop AVENO" } },
  { match: "/contact", text: "Want a faster answer? Ask me right here — I reply instantly.", openChat: true },
];

export default function SiteGuide() {
  const [open, setOpen] = useState(false);
  const [chatOpen, setChatOpen] = useState(false);
  const [nudge, setNudge] = useState(null);
  const [step, setStep] = useState(0);
  const [typed, setTyped] = useState("");
  const [talking, setTalking] = useState(false);
  const timerRef = useRef(null);
  const { pathname } = useLocation();

  // Entrance: slide in shortly after load, unless the tour already ran.
  useEffect(() => {
    if (localStorage.getItem(STORAGE_KEY) === "dismissed") return;
    const t = setTimeout(() => setOpen(true), 2200);
    return () => clearTimeout(t);
  }, []);

  // Typewriter + talking animation for the current message.
  useEffect(() => {
    if (!open) return;
    const text = TOUR[step].text;
    setTyped("");
    setTalking(true);
    let i = 0;
    const typer = setInterval(() => {
      i += 2;
      setTyped(text.slice(0, i));
      if (i >= text.length) {
        clearInterval(typer);
        setTalking(false);
        // Auto-advance after a comfortable read.
        timerRef.current = setTimeout(() => setStep((s) => Math.min(s + 1, TOUR.length - 1)), 6500);
      }
    }, 24);
    return () => { clearInterval(typer); clearTimeout(timerRef.current); };
  }, [open, step]);

  // Any close ends the tour permanently — from here on he's the assistant.
  const close = () => {
    setOpen(false);
    localStorage.setItem(STORAGE_KEY, "dismissed");
  };

  const onBubbleClick = () => {
    setNudge(null);
    if (localStorage.getItem(STORAGE_KEY) === "dismissed") {
      setChatOpen(true);
    } else {
      setStep(0);
      setOpen(true);
    }
  };

  // Contextual nudge per page — chat mode only, once per page per session.
  useEffect(() => {
    if (open || chatOpen) return;
    if (localStorage.getItem(STORAGE_KEY) !== "dismissed") return;
    const tip = NUDGES.find((n) => pathname === n.match || pathname.startsWith(n.match + "/"));
    if (!tip) return;
    let shown = [];
    try { shown = JSON.parse(sessionStorage.getItem(NUDGED_KEY) || "[]"); } catch {}
    if (shown.includes(tip.match)) return;
    const t = setTimeout(() => {
      setNudge(tip);
      try { sessionStorage.setItem(NUDGED_KEY, JSON.stringify([...shown, tip.match])); } catch {}
    }, 5000);
    return () => clearTimeout(t);
  }, [pathname, open, chatOpen]);

  // Auto-dismiss the nudge after a while.
  useEffect(() => {
    if (!nudge) return;
    const t = setTimeout(() => setNudge(null), 12000);
    return () => clearTimeout(t);
  }, [nudge]);

  const tourDone = typeof window !== "undefined" && localStorage.getItem(STORAGE_KEY) === "dismissed";
  const current = TOUR[step];
  const last = step === TOUR.length - 1;

  return (
    <>
      {/* Mascot bubble — opens the tour the first time, the chat after that */}
      <AnimatePresence>
        {!open && !chatOpen && (
          <motion.button
            key="guide-bubble"
            initial={{ scale: 0, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0, opacity: 0 }}
            transition={{ delay: 0.4, type: "spring", stiffness: 260, damping: 18 }}
            onClick={onBubbleClick}
            data-testid="site-guide-bubble"
            aria-label={tourDone ? "Chat with Julius" : "Open the site tour with Julius"}
            className="fixed bottom-24 left-5 z-40 w-12 h-12 rounded-full overflow-hidden border-2 border-primary soft-shadow-lg hover:scale-105 active:scale-95 transition-transform bg-[#FAF7F2]"
          >
            <img src="/mascot.png" alt="" className="w-full h-full object-cover" />
          </motion.button>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {open && (
          <motion.div
            key="guide"
            initial={{ x: -120, opacity: 0 }}
            animate={{ x: 0, opacity: 1 }}
            exit={{ x: -120, opacity: 0 }}
            transition={{ type: "spring", stiffness: 200, damping: 22 }}
            className="fixed bottom-24 left-5 z-40 flex items-end gap-3 max-w-[92vw]"
            data-testid="site-guide"
          >
            {/* Mascot — bobs while "talking" */}
            <motion.div
              animate={talking ? { y: [0, -5, 0, -3, 0], rotate: [0, -2, 1, -1, 0] } : { y: 0 }}
              transition={talking ? { duration: 0.9, repeat: Infinity, ease: "easeInOut" } : { duration: 0.2 }}
              className="w-16 h-16 md:w-20 md:h-20 rounded-full overflow-hidden border-2 border-primary soft-shadow-lg flex-shrink-0 bg-[#FAF7F2]"
              data-testid="site-guide-mascot"
            >
              <img src="/mascot.png" alt="Julius, your guide" className="w-full h-full object-cover" data-i18n-skip="true" />
            </motion.div>

            {/* Speech bubble */}
            <div className="relative bg-card border border-border soft-shadow-lg p-4 md:p-5 w-[270px] md:w-[320px]" data-testid="site-guide-bubble-box">
              <span className="absolute -left-2 bottom-6 w-4 h-4 bg-card border-l border-b border-border rotate-45" />
              <div className="flex items-start justify-between gap-2 mb-1">
                <p className="font-mono text-[10px] uppercase tracking-widest text-primary" data-i18n-skip="true">Julius · your guide</p>
                <button onClick={close} aria-label="Close tour" data-testid="site-guide-close"
                  className="p-1.5 -mt-1 -mr-1 text-muted-foreground hover:text-primary transition-colors">
                  <X className="w-3.5 h-3.5" />
                </button>
              </div>
              <p className="text-sm leading-relaxed min-h-[66px]" data-testid="site-guide-text">
                {typed}
                {talking && <span className="inline-block w-1.5 h-3.5 bg-primary/70 ml-0.5 animate-pulse" />}
              </p>
              <div className="mt-2.5 flex items-center justify-between gap-2">
                {current.link ? (
                  <Link to={current.link.to} onClick={close} data-testid="site-guide-link"
                    className="inline-flex items-center gap-1.5 text-xs font-semibold text-primary link-underline">
                    {current.link.label} <ArrowRight className="w-3 h-3" />
                  </Link>
                ) : <span />}
                <div className="flex items-center gap-2">
                  <span className="font-mono text-[10px] text-muted-foreground">{step + 1}/{TOUR.length}</span>
                  {!last ? (
                    <button onClick={() => { clearTimeout(timerRef.current); setStep((s) => s + 1); }} data-testid="site-guide-next"
                      className="text-xs font-medium border border-border px-2.5 py-1 hover:border-primary hover:text-primary transition-colors">
                      Next
                    </button>
                  ) : (
                    <button onClick={close} data-testid="site-guide-done"
                      className="text-xs font-medium border border-primary text-primary px-2.5 py-1 hover:bg-primary hover:text-white transition-colors">
                      Done
                    </button>
                  )}
                </div>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Contextual nudge — small tip bubble above the mascot */}
      <AnimatePresence>
        {nudge && !open && !chatOpen && (
          <motion.div
            key={`nudge-${nudge.match}`}
            initial={{ opacity: 0, y: 12, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 12, scale: 0.95 }}
            transition={{ type: "spring", stiffness: 260, damping: 20 }}
            className="fixed bottom-40 left-5 z-40 w-[240px] bg-card border border-border soft-shadow-lg p-3.5"
            data-testid="mascot-nudge"
          >
            <div className="flex items-start justify-between gap-2 mb-1">
              <p className="font-mono text-[10px] uppercase tracking-widest text-primary" data-i18n-skip="true">Julius · tip</p>
              <button onClick={() => setNudge(null)} aria-label="Dismiss tip" data-testid="mascot-nudge-close"
                className="p-1 -mt-1 -mr-1 text-muted-foreground hover:text-primary transition-colors">
                <X className="w-3.5 h-3.5" />
              </button>
            </div>
            <p className="text-xs leading-relaxed" data-testid="mascot-nudge-text">{nudge.text}</p>
            <div className="mt-2">
              {nudge.openChat ? (
                <button onClick={() => { setNudge(null); setChatOpen(true); }} data-testid="mascot-nudge-chat"
                  className="inline-flex items-center gap-1.5 text-xs font-semibold text-primary link-underline">
                  Ask me <ArrowRight className="w-3 h-3" />
                </button>
              ) : (
                <Link to={nudge.link.to} onClick={() => setNudge(null)} data-testid="mascot-nudge-link"
                  className="inline-flex items-center gap-1.5 text-xs font-semibold text-primary link-underline">
                  {nudge.link.label} <ArrowRight className="w-3 h-3" />
                </Link>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Assistant mode — the only chat entry point once the tour has run */}
      <Chatbot open={chatOpen} onClose={() => setChatOpen(false)} />
    </>
  );
}
